import { doc, getDocFromCache } from 'firebase/firestore'
import { captureException, captureMessage } from '@sentry/core'
import platform from 'modules/platform'
import { isIndexedDBError } from 'modules/root/indexeddb'
import { db } from './index'

/**
 * Recovers from an IndexedDB failure that stops Firestore, which is every one of them.
 *
 * Firestore runs all of its work through one async queue. The queue chains each operation onto the
 * promise that the last one returned, so a rejection there becomes the tail that every later
 * operation chains onto, and none of them ever run. Writes, listeners and the sync all stop, and
 * nothing reports that, because each call simply never completes.
 *
 * Firestore recovers only from the errors that it wraps in an IndexedDbTransactionError. It does not
 * wrap the bare DOMExceptions that iOS produces (see isIndexedDBError), so those reach the queue and
 * poison it. A reload is the only recovery, because the same dead queue has to run terminate(), so
 * that call never returns either.
 */

/** How long Firestore has to answer a read from its cache before we call its queue dead. */
const PROBE_TIMEOUT = 15000

/** Reloads inside this window count towards RELOAD_LIMIT, so a failure that repeats can't loop. */
const RELOAD_WINDOW = 10 * 60 * 1000
const RELOAD_LIMIT = 2
const RELOADS_KEY = 'maqasa.firestoreRecoveryReloads'

let installed = false
let checking = false

/** Watches for the IndexedDB failures that stop Firestore. It installs once, and later calls do nothing. */
export function installFirestoreRecovery() {
	if (installed) {
		return
	}
	installed = true

	window.addEventListener('unhandledrejection', (ev) => {
		if (!isIndexedDBError(ev.reason)) {
			return
		}

		handleIndexedDBError(ev.reason)
	})
}

async function handleIndexedDBError(reason: unknown) {
	/* One error stops the whole queue, and we can get more than one report of it. Only the first
	   report matters, and a second health check would wait for the same timeout again.
	 */
	if (checking) {
		return
	}
	checking = true

	try {
		console.warn('DATABASE: IndexedDB failed, checking whether Firestore still works', reason)
		captureException(reason)

		if (await firestoreAnswersFromCache()) {
			console.warn('DATABASE: Firestore still works')
			return
		}

		console.error('DATABASE: Firestore has stopped working after an IndexedDB error', reason)
		await restart()
	} finally {
		checking = false
	}
}

/**
 * Reports whether Firestore still runs the work that we give it. A read from the cache goes through
 * the same queue as everything else. The document does not have to exist, because an answer of
 * either kind shows that the queue still runs. A failed queue never answers at all.
 */
function firestoreAnswersFromCache(): Promise<boolean> {
	if (!db) {
		return Promise.resolve(true)
	}

	let read: Promise<boolean>
	try {
		read = getDocFromCache(doc(db, 'healthCheck', 'firestoreQueue')).then(() => true, () => true)
	} catch (error) {
		/* A terminated client refuses the read here rather than from the queue, which is still an answer. */
		return Promise.resolve(true)
	}

	const timeout = new Promise<boolean>(resolve => window.setTimeout(() => resolve(false), PROBE_TIMEOUT))
	return Promise.race([read, timeout])
}

async function restart() {
	if (!recordReload()) {
		console.error('DATABASE: too many reloads, leaving the app to the user')
		captureMessage('Firestore recovery gave up after repeated reloads')

		await platform.alert('Maqasa has lost access to the storage on this device, so it has stopped saving and syncing your work. Please close the app completely and open it again.\n\nIf this message comes back, please contact support.')
		return
	}

	/* The alert also gives the app time to save its state before the reload. */
	await platform.alert('Maqasa has lost access to the storage on this device, so it has stopped saving and syncing your work. The app will reload now.')
	window.location.reload()
}

/**
 * Records that we are about to reload, and reports whether we should. A reload that does not fix the
 * problem would otherwise return the app to this point.
 */
function recordReload(): boolean {
	const now = Date.now()
	let reloads: number[] = []

	try {
		const stored = window.localStorage.getItem(RELOADS_KEY)
		if (stored) {
			reloads = (JSON.parse(stored) as number[]).filter(when => now - when < RELOAD_WINDOW)
		}
	} catch (error) {
		console.warn('DATABASE: failed to read the record of recovery reloads', error)
	}

	if (reloads.length >= RELOAD_LIMIT) {
		return false
	}

	try {
		window.localStorage.setItem(RELOADS_KEY, JSON.stringify([...reloads, now]))
	} catch (error) {
		console.warn('DATABASE: failed to record a recovery reload', error)
	}
	return true
}
